import { ApolloError, WatchQueryFetchPolicy } from '@apollo/client';
import {
  LineageFetchPolicy,
  PageInfo,
  useActionExecutionsQuery,
  useConversationActionExecutionUpdatedSubscription,
} from '@shared/generated/graphql';
import { actionExecutionFromFragment } from '@shared/graphql/fromFragments/actionExecution';
import { ActionExecution } from '@shared/types/action-execution';
import { useEffect, useMemo, useRef } from 'react';

const ACTION_EXECUTION_UPDATE_REFETCH_DELAY_MS = 150;

type UseActionExecutionsOptions = {
  organizationIds: string[];
  conversationIds?: string[];
  lineageFetchPolicy?: LineageFetchPolicy;
  pollInterval?: number;
  subscribeToUpdates?: boolean;
  after?: string | null;
  fetchPolicy?: WatchQueryFetchPolicy;
};

type UseActionExecutionsValue = {
  actionExecutions: ActionExecution[];
  isLoading: boolean;
  error?: ApolloError;
  pageInfo?: PageInfo;
};

export const useActionExecutions = (
  props: UseActionExecutionsOptions
): UseActionExecutionsValue => {
  const {
    organizationIds,
    conversationIds,
    lineageFetchPolicy,
    pollInterval,
    subscribeToUpdates = false,
    after,
    fetchPolicy,
  } = props;
  const { data, loading, error, refetch } = useActionExecutionsQuery({
    variables: {
      input: { organizationIds, conversationIds, lineageFetchPolicy },
      after,
    },
    fetchPolicy,
    pollInterval,
  });
  const { data: conversationActionExecutionUpdateData } =
    useConversationActionExecutionUpdatedSubscription({
      skip:
        !subscribeToUpdates ||
        organizationIds.length !== 1 ||
        !conversationIds?.length,
      variables: {
        conversationIds: conversationIds ?? [],
        organizationId: organizationIds[0] ?? '',
      },
    });
  const conversationActionExecutionUpdate =
    conversationActionExecutionUpdateData?.conversationActionExecutionUpdated;
  const handledConversationActionExecutionUpdate = useRef(
    conversationActionExecutionUpdate
  );
  const refetchTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  );

  useEffect(() => {
    if (
      !conversationActionExecutionUpdate ||
      handledConversationActionExecutionUpdate.current ===
        conversationActionExecutionUpdate
    ) {
      return;
    }
    handledConversationActionExecutionUpdate.current =
      conversationActionExecutionUpdate;
    clearTimeout(refetchTimeout.current);
    refetchTimeout.current = setTimeout(() => {
      refetch().catch(console.error);
    }, ACTION_EXECUTION_UPDATE_REFETCH_DELAY_MS);
  }, [conversationActionExecutionUpdate, refetch]);

  useEffect(
    () => () => {
      clearTimeout(refetchTimeout.current);
    },
    []
  );

  const actionExecutions = useMemo(() => {
    return data?.actionExecutions.edges.map((edge) =>
      actionExecutionFromFragment(edge.node)
    );
  }, [data?.actionExecutions]);

  return {
    actionExecutions: actionExecutions ?? [],
    isLoading: loading,
    pageInfo: data?.actionExecutions.pageInfo,
    error,
  };
};
